import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

export interface PlickState {
    id: number;
    transactionId: string;
    senderId: string;
    senderFirstname: string;
    senderLastname: string;
    senderIsCompany: boolean;
    senderCompany: string;
    senderDevice: string;
    senderAccount: string;
    senderLanguagePreferences: string;
    senderExtraInfo: string | null;
    receiverName: string;
    receiverDevice: string;
    receiverIban: string;
    receiverIbanFromBank: boolean;
    idBank: string;
    activationDate: string;
    amount: number;
    amountString: string;
    causal: string;
    notes: string;
    pin: string | null;
    pinType: string;
    idLogo: string;
    correctPlick: true;
    status: string;
    plickType: string;
    receiverType: string | null;
    fileDataDto: null;
    fileId: null;
    validationErrors: string;
    validationErrorsList: null;
    bankCro: null;
    bankExecutionDate: null;
    currencyDate: null;
    transferStatus: null;
    plickIdFromPortal: string | null;
    fileVersion: number;
    checkIbanCode: null;
    checkIban: boolean;
    checkIbanLastStatus: null;
    voucher: boolean;
}

export interface SortElement {
    sorted: boolean;
    unsorted: boolean;
    empty: boolean;
}

export interface PageableElement {
    sort: SortElement;
    offset: number;
    pageNumber: number;
    pageSize: number;
    paged: boolean;
    unpaged: boolean;
}

export interface PageElement {
    content: PlickState[];
    pageable: PageableElement;
    totalElements: number;
    totalPages: number;
    last: boolean;
    size: number;
    number: number;
    first: boolean;
    numberOfElements: number;
    empty: boolean;
}

export interface FileDetailResponse {
    nextUrl: null | string;
    page: PageElement;
}

export interface PlicksListState {
    loading: boolean;
    error: undefined | AxiosError<string>;
    loadingFetch: boolean;
    errorFetch: undefined | AxiosError<string>;
    list: FileDetailResponse;
}

export interface FileDetailPayload {
    bankId: string;
    fileId: string;
    filter?: any | undefined;
    sort?: any | undefined;
    page?: number | undefined;
    pageSize?: number | undefined;
}

export interface FetchFileDetailPayload {
    url: string;
}

let cancelTokenFetch = undefined;

export const getFileDetail = createAsyncThunk<FileDetailResponse, FileDetailPayload, { rejectValue: string }>(
    'file/getDetail',
    async ({ bankId, fileId, page, pageSize, filter, sort }: FileDetailPayload, { rejectWithValue, dispatch }) => {
        try {
            if (cancelTokenFetch) {
                cancelTokenFetch.cancel('Operazione cancellata');
                cancelTokenFetch = null;
            }

            const config: AxiosRequestConfig = {
                headers: {
                    'Content-Type': 'application/json'
                },
                withCredentials: true
            };

            const baseUrl = `${process.env.REACT_APP_FILE_SERVICE_API_URL}/fileservice/apifile/v2/${bankId}/${fileId}/plicks`;
            const parameters = new URLSearchParams();

            if (page === 0 || page) {
                parameters.append('page', page.toString());
            }
            if (pageSize) {
                parameters.append('size', pageSize.toString());
            }
            if (filter && Object.keys(filter).length > 0) {
                parameters.append('filter', JSON.stringify(filter));
            }
            if (sort) {
                parameters.append('sort', sort);
            }

            const response: AxiosResponse<FileDetailResponse> = await axios.get(`${baseUrl}${parameters ? '?' + parameters.toString() : ''}`, config);
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError<FileDetailResponse>;
            return rejectWithValue(axiosError?.response?.data ?? { message: 'error.generic' });
        }
    }
);

export const fetchFileDetail = createAsyncThunk<FileDetailResponse, FetchFileDetailPayload, { rejectValue: string }>(
    'file/fetchFileDetail',
    async ({ url }: FetchFileDetailPayload, { dispatch, rejectWithValue }) => {
        try {
            cancelTokenFetch = axios.CancelToken.source();

            const config: AxiosRequestConfig = {
                headers: {
                    'Content-Type': 'application/json'
                },
                cancelToken: cancelTokenFetch.token,
                withCredentials: true
            };
            const response: AxiosResponse<FileDetailResponse> = await axios.get(url, config);
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError<FileDetailResponse>;
            return rejectWithValue(axiosError?.response?.data ?? { message: 'error.generic' });
        }
    }
);
